"use client";

import { CloseRounded } from '@mui/icons-material';
import { ReactNode, useEffect, useState } from 'react';
import { BANNER_END_DATE, BANNER_STORAGE_KEY } from '~/consts/app';

interface FeedbackBannerProps {
  readonly message?: ReactNode;
  readonly context?: string;
  readonly color?: string;
  readonly border?: string;
  readonly endDate?: Date;
}

export function FeedbackBanner({
  message = "Thank you for your feedback—this update reflects suggestions from our CSRs.",
  context = 'consumer',
  color = 'bg-brand-dte-dteblue',
  border = 'border-status-success-Default',
  endDate,
}: FeedbackBannerProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if banner should be displayed
    const contextKey = `${BANNER_STORAGE_KEY}-${context}`;
    const isDismissed = localStorage.getItem(contextKey) === 'true';
    const targetEndDate = endDate || BANNER_END_DATE;
    const isBeforeEndDate = new Date() <= targetEndDate;

    // Don't show CSR banner in popup contexts to prevent duplicates
    const isPopupContext = window.location.pathname.includes('popup-info');
    const shouldHideInPopup = context === 'csr' && isPopupContext;

    if (!isDismissed && isBeforeEndDate && !shouldHideInPopup) {
      setIsVisible(true);
    }
  }, [context]);

  const handleDismiss = () => {
    const contextKey = `${BANNER_STORAGE_KEY}-${context}`;
    localStorage.setItem(contextKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`${color} text-brand-background-white ${border} opacity-100 flex items-center justify-between mx-auto w-full min-h-[56px] h-auto py-2`}>
      <div className="flex items-center justify-center gap-2 flex-1 px-4">
        <span className="text-sm text-center">
          {typeof message === 'string' && message.includes('—') ? (
            <>
              <span className="font-bold">{message.split('—')[0]}</span>
              <span className="font-medium">—{message.split('—')[1]}</span>
            </>
          ) : (
            <span className="font-medium">{message}</span>
          )}
        </span>
      </div>
      <button
        onClick={handleDismiss}
        className="p-2 hover:bg-white/20 active:bg-white/30 rounded-full mr-4 flex-shrink-0 w-10 h-10 flex items-center justify-center transition-colors"
        aria-label="Dismiss banner"
      >
        <CloseRounded className="h-4 w-4" />
      </button>
    </div>
  );
}